/**
 * Angebote-Subdomain Warmup operator panel.
 *
 * Embedded at the top of `FtTab` so it's visible regardless of which inner
 * tab the operator is on. During the 10-week warmup pilot (started 2026-06-25)
 * the operator marks at most one FT-SAM material per day via this panel; the
 * scheduler routes the next pick of that material through angebote.* and
 * clears the flag post-send. Audit trail lives in `mc_automated_sams.sent_via_angebote`.
 *
 * Wire-up:
 *   - Backend routes in `node-app/src/routes/ft-sams.ts`:
 *       GET    /api/adserver/ft-sams/angebote-warmup/flagged
 *       GET    /api/adserver/ft-sams/angebote-warmup/lookup?q=<text>
 *       POST   /api/adserver/ft-sams/angebote-warmup/flag/:samId  body: {enabled: boolean}
 *   - Iterable artifacts provisioned by `node-app/src/scripts/setup-angebote-channel.ts`:
 *       channelId=4971, messageTypeId=7953, templateId=681182, listId=353716
 *   - Scheduler routing in `node-app/src/services/FtSamSchedulingService.ts:scheduleSam`
 *
 * Stays embedded in FtTab (not a standalone page) because:
 *   - Warmup is a temporary phase (~10 weeks); a dedicated nav entry would be
 *     dead weight afterwards
 *   - Operator's mental flow during warmup is "check planned sends, mark one
 *     for warmup, return to other admin work" — co-locating reduces context
 *     switches vs a separate tab
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { api } from '../../helpers/apiUtils';

interface FlaggedRow {
  sam_id: number;
  subject: string | null;
  from_name: string | null;
  product_id: number;
  customer_id: string;
  product_name: string | null;
}

interface LookupRow extends FlaggedRow {
  use_angebote_channel: number;
}

const AngeboteWarmupSection: React.FC = () => {
  const [flagged, setFlagged] = useState<FlaggedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResults, setLookupResults] = useState<LookupRow[]>([]);
  const [searching, setSearching] = useState(false);

  const loadFlagged = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/adserver/ft-sams/angebote-warmup/flagged');
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Unbekannter Fehler beim Laden');
      }
      setFlagged(res.data.data || []);
    } catch (err) {
      setError((err as Error).message ?? String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/adserver/ft-sams/angebote-warmup/flagged');
        if (cancelled) return;
        if (!res.data?.success) {
          throw new Error(res.data?.error || 'Unbekannter Fehler beim Laden');
        }
        setFlagged(res.data.data || []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runLookup = useCallback(async () => {
    const q = lookupQuery.trim();
    if (q.length < 2) {
      setError('Suchbegriff muss mindestens 2 Zeichen lang sein');
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const res = await api.get(
        `/api/adserver/ft-sams/angebote-warmup/lookup?q=${encodeURIComponent(q)}`,
      );
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Unbekannter Fehler bei der Suche');
      }
      setLookupResults(res.data.data || []);
    } catch (err) {
      setError((err as Error).message ?? String(err));
    } finally {
      setSearching(false);
    }
  }, [lookupQuery]);

  const setFlag = useCallback(
    async (samId: number, enabled: boolean) => {
      setError(null);
      setInfo(null);
      try {
        const res = await api.post(`/api/adserver/ft-sams/angebote-warmup/flag/${samId}`, {
          enabled,
        });
        if (!res.data?.success) {
          throw new Error(res.data?.error || 'Toggle fehlgeschlagen');
        }
        setInfo(
          enabled
            ? `SAM #${samId} für nächsten Warmup-Send via angebote.* markiert. Flag clears automatically after schedule.`
            : `SAM #${samId} Warmup-Flag manuell gelöscht.`,
        );
        await loadFlagged();
        if (lookupResults.length > 0) {
          setLookupResults((prev) =>
            prev.map((r) =>
              r.sam_id === samId ? { ...r, use_angebote_channel: enabled ? 1 : 0 } : r,
            ),
          );
        }
      } catch (err) {
        setError((err as Error).message ?? String(err));
      }
    },
    [loadFlagged, lookupResults.length],
  );

  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        backgroundColor: '#fff8e1',
        border: '1px solid #f57c00',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">🔥 Angebote-Subdomain Warmup (Phase 1a)</Typography>
        <Button size="small" onClick={loadFlagged} disabled={loading}>
          {loading ? <CircularProgress size={16} /> : 'Refresh'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Markiere maximal 1 SAM pro Tag. Wird via Channel 4971 / Template 681182 / Liste 353716
        versendet. Flag wird nach erfolgreichem Schedule automatisch gelöscht.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Aktuell markierte SAMs:
        </Typography>
        {flagged.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Keine SAM für angebote.* markiert. Heute läuft alles über news.* (Default).
          </Typography>
        ) : (
          flagged.map((row) => (
            <Box
              key={row.sam_id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.5,
                p: 1,
                backgroundColor: '#ffffff',
                borderRadius: 1,
              }}
            >
              <Chip label={`SAM #${row.sam_id}`} color="warning" size="small" />
              <Chip label={row.customer_id} size="small" variant="outlined" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.subject || '(kein Subject)'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.product_name ? `${row.product_name} • ` : ''}#{row.product_id}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => setFlag(row.sam_id, false)}
              >
                Flag löschen
              </Button>
            </Box>
          ))
        )}
        {flagged.length > 1 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Mehr als 1 SAM markiert. Im Warmup sollte nur 1 Flag aktiv sein — bitte überprüfen.
          </Alert>
        )}
      </Box>

      <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          SAM für nächsten Send markieren:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="SAM-ID, Subject oder Produktname"
            value={lookupQuery}
            onChange={(e) => setLookupQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runLookup();
            }}
          />
          <Button variant="contained" onClick={runLookup} disabled={searching}>
            {searching ? <CircularProgress size={16} /> : 'Suchen'}
          </Button>
        </Box>

        {lookupResults.length > 0 && (
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['SAM', 'Kunde', 'Subject', 'Produkt', 'Status', 'Aktion'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lookupResults.map((row) => (
                  <TableRow key={row.sam_id} hover>
                    <TableCell>#{row.sam_id}</TableCell>
                    <TableCell>
                      <Chip label={row.customer_id} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{row.subject || '—'}</TableCell>
                    <TableCell>
                      {row.product_name ? (
                        <>
                          {row.product_name}
                          <Typography variant="caption" display="block" color="text.secondary">
                            #{row.product_id}
                          </Typography>
                        </>
                      ) : (
                        `#${row.product_id}`
                      )}
                    </TableCell>
                    <TableCell>
                      {row.use_angebote_channel === 1 ? (
                        <Chip label="Markiert" color="warning" size="small" />
                      ) : (
                        <Chip label="Aus" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.use_angebote_channel === 1 ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setFlag(row.sam_id, false)}
                        >
                          Löschen
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="warning"
                          variant="contained"
                          onClick={() => setFlag(row.sam_id, true)}
                        >
                          Markieren
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
};

export default AngeboteWarmupSection;
